import React from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';

import { Toast } from '@/nova/components';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { useToast } from '@/nova/hooks/useToast';

import { assetPath } from '../LandingPageComposer/link-for';
import type { TrackingLinkKind } from '../../tracking-links-api';
import { AssetComposerOverlay } from './AssetComposerOverlay';
import {
  ASSET_OVERLAY_PARAM,
  assetKindOpens,
  assetRefFromPath,
  formatAssetMarker,
  parseAssetMarker,
  type AssetRef,
} from './asset-overlay';

export interface UseAssetOverlayOptions {
  /** Show the overlay's notices through the page's own toast (else the overlay brings its own). */
  onNotify?: (message: string) => void;
  /** The overlay closed (the page may re-read what the composer could have changed). */
  onClosed?: () => void;
}

export interface UseAssetOverlay {
  /** Open an asset in place (or, while its route's flag is off, navigate to its page). */
  open: (kind: TrackingLinkKind, creativeId: string) => void;
  /** Open an in-app path: an asset composer path opens in place, any other path navigates. */
  openPath: (path: string) => void;
  /** Mount once in the page: the overlay while the URL carries its marker (plus its toast when the page has none). */
  node: React.ReactNode;
}

/** The history state key telling an entry this hook pushed (closing it goes back instead of adding an entry). */
const PUSHED_KEY = 'owsAssetOverlay';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const pushedHere = (state: unknown): boolean => isRecord(state) && state[PUSHED_KEY] === true;

/** The location's search with the marker set (or removed, with `ref` null). Pure. */
const searchWith = (search: string, ref: AssetRef | null): string => {
  const q = new URLSearchParams(search);
  if (ref) q.set(ASSET_OVERLAY_PARAM, formatAssetMarker(ref));
  else q.delete(ASSET_OVERLAY_PARAM);
  const out = q.toString();
  return out ? `?${out}` : '';
};

/**
 * Opens marketing assets IN PLACE over the current page: `open()` adds `?asset=<kind>:<id>` to the current address (a
 * new history entry, so browser Back closes it; opening another while one is open swaps it in the same entry), and the
 * page renders `node` once, which mounts {@link AssetComposerOverlay} while the marker is there. Closing goes back when
 * this hook pushed the entry, else (a reload, a shared address) strips the marker in place. A reload or back / forward
 * therefore keeps the overlay. Links keep their real hrefs: callers intercept only plain left clicks (`openInApp`), so a
 * modified or middle click still opens the asset's own page in a new tab.
 */
export const useAssetOverlay = (options: UseAssetOverlayOptions = {}): UseAssetOverlay => {
  const location = useLocation();
  const navigate = useNavigate();
  const gate = useFeatureGate();
  const { toast, show } = useToast();

  // The latest location and callbacks for the stable actions below (a page passes fresh options each render).
  const locationRef = React.useRef(location);
  locationRef.current = location;
  const onClosedRef = React.useRef(options.onClosed);
  onClosedRef.current = options.onClosed;

  // The location the overlay was closed at: it hides at once, before the router has answered the close (a `back` lands
  // a moment later), and any later location (a new open, Forward) shows the marker again. Derived, not synced.
  const [closedAt, setClosedAt] = React.useState<Location | null>(null);

  const open = React.useCallback(
    (kind: TrackingLinkKind, creativeId: string): void => {
      if (!assetKindOpens(kind, gate.isEnabled)) {
        navigate(assetPath(kind, creativeId));
        return;
      }
      const loc = locationRef.current;
      const already = parseAssetMarker(new URLSearchParams(loc.search).get(ASSET_OVERLAY_PARAM));
      const to = { pathname: loc.pathname, search: searchWith(loc.search, { kind, id: creativeId }), hash: loc.hash };
      const state: Record<string, unknown> = isRecord(loc.state) ? { ...loc.state } : {};
      if (already) navigate(to, { replace: true, state: loc.state });
      else navigate(to, { state: { ...state, [PUSHED_KEY]: true } });
    },
    [gate, navigate],
  );

  const openPath = React.useCallback(
    (path: string): void => {
      const ref = assetRefFromPath(path);
      if (ref) open(ref.kind, ref.id);
      else navigate(path);
    },
    [open, navigate],
  );

  const close = React.useCallback((): void => {
    const loc = locationRef.current;
    setClosedAt(loc);
    if (pushedHere(loc.state)) navigate(-1);
    else navigate({ pathname: loc.pathname, search: searchWith(loc.search, null), hash: loc.hash }, { replace: true, state: loc.state });
    onClosedRef.current?.();
  }, [navigate]);

  /** Leave for a page: the overlay's entry becomes that page when this hook pushed it (Back then returns to the page
   *  without the overlay); otherwise the marker is stripped first and the page pushed. */
  const leaveTo = React.useCallback(
    (path: string): void => {
      const loc = locationRef.current;
      if (pushedHere(loc.state)) {
        navigate(path, { replace: true });
        return;
      }
      navigate({ pathname: loc.pathname, search: searchWith(loc.search, null), hash: loc.hash }, { replace: true, state: loc.state });
      navigate(path);
    },
    [navigate],
  );

  const marker = parseAssetMarker(new URLSearchParams(location.search).get(ASSET_OVERLAY_PARAM));
  // A marker whose kind is off (a stale address) opens nothing.
  const shown = marker && closedAt !== location && assetKindOpens(marker.kind, gate.isEnabled) ? marker : null;
  const notify = options.onNotify ?? show;

  const node = (
    <>
      {shown ? (
        <AssetComposerOverlay
          key={formatAssetMarker(shown)}
          kind={shown.kind}
          creativeId={shown.id}
          onClose={close}
          onOpenPage={() => leaveTo(assetPath(shown.kind, shown.id))}
          onNotify={notify}
        />
      ) : null}
      {options.onNotify ? null : <Toast toast={toast} />}
    </>
  );

  return { open, openPath, node };
};

export default useAssetOverlay;
